// socket.js - ENHANCED VERSION WITH DEBUG LOGGING
import { io } from "socket.io-client";

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 
                   process.env.REACT_APP_API_URL?.replace(/\/$/, "") || 
                   "http://localhost:3001";

// Debug logging utility
const socketLogger = (type, message, data = {}) => {
  const timestamp = new Date().toISOString();
  const styles = {
    connect: 'color: green; font-weight: bold',
    disconnect: 'color: red; font-weight: bold',
    error: 'color: orange; font-weight: bold',
    event: 'color: blue; font-weight: bold',
    typing: 'color: purple; font-weight: bold',
    message: 'color: teal; font-weight: bold',
    debug: 'color: gray; font-weight: normal'
  };
  
  console.log(`%c[Socket ${type.toUpperCase()}] ${timestamp}`, styles[type] || 'color: black', message, data);
};

// Get token safely
const getToken = () => {
  if (typeof window === "undefined") return null;
  try {
    const authRaw = localStorage.getItem("authUser");
    if (authRaw) {
      const auth = JSON.parse(authRaw);
      const token = auth?.token || null;
      socketLogger('debug', 'Token retrieved', { hasToken: !!token, userId: auth?.user?._id || auth?._id });
      return token;
    }
  } catch (error) {
    socketLogger('error', 'Error getting auth token', { error: error.message });
  }
  return null;
};

// Create socket instance with better configuration
export const socket = io(SOCKET_URL, {
  transports: ["websocket", "polling"],
  withCredentials: true,
  auth: {
    token: getToken()
  },
  reconnection: true,
  reconnectionAttempts: 10,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  timeout: 20000,
  forceNew: false,
  autoConnect: false
});

// Enhanced socket event listeners for connection management
socket.on("connect", () => {
  socketLogger('connect', 'Socket connected successfully', { 
    socketId: socket.id,
    connected: socket.connected,
    authenticated: !!socket.auth.token
  });
  
  // Re-join any necessary rooms after reconnection
  const authRaw = localStorage.getItem("authUser");
  if (authRaw) {
    try {
      const auth = JSON.parse(authRaw);
      const userId = auth?.user?._id || auth?._id;
      if (userId) {
        socketLogger('debug', 'Rejoining user room after connection', { userId });
        socket.emit("joinUser", { userId });
      }
    } catch (error) {
      socketLogger('error', 'Error rejoining user room', { error: error.message });
    }
  }
});

socket.on("disconnect", (reason) => {
  socketLogger('disconnect', 'Socket disconnected', { reason });
});

socket.on("connect_error", (error) => {
  socketLogger('error', 'Socket connection error', { 
    error: error.message,
    description: error.description,
    context: error.context
  });
});

socket.on("reconnect", (attemptNumber) => {
  socketLogger('connect', 'Socket reconnected', { attemptNumber });
});

socket.on("reconnect_attempt", (attemptNumber) => {
  socketLogger('debug', 'Socket reconnection attempt', { attemptNumber });
});

socket.on("reconnect_error", (error) => {
  socketLogger('error', 'Socket reconnection error', { error: error.message });
});

socket.on("reconnect_failed", () => {
  socketLogger('error', 'Socket reconnection failed');
});

// Add global socket event logger
const originalEmit = socket.emit;
socket.emit = function(event, data) {
  socketLogger('event', `EMITTING EVENT: ${event}`, data);
  return originalEmit.call(this, event, data);
};

// Log all incoming events
const originalOn = socket.on;
socket.on = function(event, handler) {
  return originalOn.call(this, event, (...args) => {
    socketLogger('event', `RECEIVED EVENT: ${event}`, args[0]);
    return handler(...args);
  });
};

export { socketLogger };